import { QueryKey, UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPost } from '../../api/axios-handler';
import { EventTypes, analytics } from '../../utils/analytics';
import useAuth from '../utils/useAuth';
import { useHubspotEventMutation } from './useEvent';
import { StickyStateKeys } from '../utils/useStickyState';

export const callQueryKeys = {
  callRequests: ['callRequests'] as const,
};

export interface Call {
  listener_role: CallListenerRole;
  listener_role_id: number;
  listener_state_id: any;
  logged_duration: number;
  package_id: any;
  pending_announcement: any;
  pending_announcement_all: any;
  phone_number: string;
  rating_caller_of_listener: any;
  recording_path: any;
  request: CallRequestData;
  state: any;
  state_id: any;
  stripe_payment_intent_id: any;
  termination_disposition: string;
  tier_id: number;
}

interface CallUser {
  display_name: string;
  first_name: string;
  id: number;
}

export interface CallListenerRole {
  id: number;
  profile_photo_file_url: string;
  profile_photo_square_file_url: string;
  user: CallUser;
}

export interface CallRequestData {
  audio_file_url: any;
  call_id: number;
  caller_role_id: number;
  connections_processed_at: any;
  created_at: string;
  id: number;
  is_backup_request: boolean;
  original_backup_request_id: any;
  original_request_id: any;
  package_id: any;
  pending_morphii_data?: unknown;
  perform_backup_requests: boolean;
  status: string;
  tag_group_ids: number[];
  text: string;
  topic: Topic;
  topic_id: number;
  updated_at: any;
}

export interface Topic {
  audio_file_path: string;
  button_image_file_path: string;
  button_image_selected_file_path: string;
  description: string;
  id: number;
  identifier: string;
  is_default: boolean;
  is_featured: boolean;
  is_primary: boolean;
  is_required: boolean;
  morphii?: string[];
  morphii_metadata?: unknown[];
  name: string;
  sample_1_audio_file_path: string;
  sample_1_theme: any;
  sample_2_audio_file_path: any;
  sample_2_theme: any;
  sort_weight: number;
  status: string;
  tag_id: number;
  theme: string;
}

export interface CallRequest {
  call_units_length: number;
  audio_duration: number;
  audio_file_url: string;
  call_id: number;
  caller_role_id: number;
  connections: Connection[];
  connections_processed_at: string;
  created_at: string;
  id: number;
  is_backup_request: boolean;
  original_backup_request_id: number;
  original_request_id: number;
  package_id: number;
  partner_code: string;
  pending_morphii_data?: unknown;
  perform_backup_requests: boolean;
  status: 'new' | 'responded' | 'backup_pending' | 'backup_found';
  text: string;
  tag_group_ids?: number[];
  topic: RequestTopic;
  topic_id: number;
  updated_at: string;
}

export interface Connection {
  created_at: string;
  evaluation_ends_at: string;
  expired: boolean;
  id: number;
  listener_acknowledged: boolean;
  listener_role: ListenerRole;
  listener_role_id: number;
  offered_at: string;
  package_id: number;
  request_id: number;
  scheduled_at: string | null;
  status: 'matched' | 'passed' | 'offered' | 'accepted' | 'not_accepted' | 'canceled' | 'on_hold';
  tier_id: number;
  updated_at: string;
}

export interface ListenerRole {
  about_me: string;
  availabilities: Availability[];
  available: boolean;
  available_now: boolean;
  background_traits: string[];
  can_take_calls: boolean;
  connection_blocked: boolean;
  favorite_id: number;
  id: number;
  is_available: boolean;
  is_active: boolean;
  is_peer_active: boolean;
  listener_audio: ListenerAudio[];
  military_tag: string;
  next_available_text: string;
  peer_status: string;
  primary_language_tag: string;
  profile_photo_file_url: string;
  profile_photo_square_file_url: string;
  profile_traits: string[];
  secondary_language_tag: string;
  share_link_url: string;
  spirituality_tag: string;
  state: string;
  status: string;
  tier_id: number;
  type: string;
  user: User;
}

export interface Availability {
  day_of_week: string;
  end_date: string;
  ends_at: string;
  start_date: string;
  starts_at: string;
}

export interface ListenerAudio {
  created_at: string;
  duration: number;
  excerpt: string;
  file_url: string;
  first_approved_at?: string;
  id: number;
  is_favorite: boolean;
  last_updated_at: string;
  listener_role_id: number;
  message_quality: number;
  title: string;
  topic_tags: TopicTag[];
}

export type TagStatus = 'APPROVED' | 'REJECTED' | 'UNGRADED';

export interface TopicTag {
  created_at: string;
  id: number;
  status: TagStatus;
  listener_audio_id: number;
  media: string;
  postcall_metadata_id: number;
  sort_order: number;
  tag: Tag;
  tag_id: number;
  user_id: number;
}

export interface Tag {
  id: number;
  is_background: boolean;
  is_default: boolean;
  is_required: boolean;
  is_visible: boolean;
  media?: TagMedia[];
  name: string;
  sort_order: number;
  tag_type: string;
}

export interface TagMedia {
  file_key: string;
  file_url: string;
}

export interface CallTopic {
  audio_file_url?: string;
  button_image_selected_url: string;
  button_image_url: string;
  description: string;
  id: number;
  identifier: string;
  is_default: boolean;
  is_featured: boolean;
  is_primary: boolean;
  is_required: boolean;
  morphii?: string[];
  morphii_metadata?: unknown[];
  name: string;
  sample_1_audio_file_url?: string;
  sample_1_theme?: string;
  sample_2_audio_file_url: string;
  sample_2_theme: string;
  sort_weight: number;
  status: string;
  tag_id: number;
  theme: string;
}

export interface User {
  display_name: string;
  first_name: string;
  pronoun: string;
}

export interface RequestTopic {
  audio_file_url: string;
  id: number;
  is_shadow_topic: boolean;
  morphii?: string[];
  morphii_metadata?: unknown[];
  name: string;
  theme: string;
}

const getActiveCalls = async (callerRoleId: number): Promise<any> => {
  return await axiosGet(`/calls/?caller_role_id=${callerRoleId}&is_active=true`, {
    include_timezone_offset: true,
  }).then((response) => response.data);
};

const getAllCalls = async (callerRoleId: number): Promise<any> => {
  return await axiosGet(`/calls/?caller_role_id=${callerRoleId}`, {
    include_timezone_offset: true,
  }).then((response) => response.data);
};

const getCallRequests = async (callerRoleId: number): Promise<CallRequest[]> => {
  return await axiosGet(`/requests/?caller_role_id=${callerRoleId}&active=true&respond_with_caller_timezone=true`, {
    include_timezone_offset: true,
  }).then((response) => response.data);
};

const getCallRequestById = async (requestId: number): Promise<CallRequest> => {
  return await axiosGet(`/requests/${requestId}?respond_with_caller_timezone=true`, {
    include_timezone_offset: true,
  }).then((response) => response.data);
};

const getCall = async (callId: number): Promise<Call> => {
  return await axiosGet(`/calls/${callId}`, {
    include_timezone_offset: true,
  }).then((response) => response.data);
};

export interface ActiveCall {
  call_rate: number;
  caller_charged_amount: any;
  caller_mobile_phone: string;
  caller_role: CallerRole;
  caller_role_id: number;
  caller_spent_amount: any;
  caller_state_id: number;
  collect_post_call_mood_metric: boolean;
  created_at: string;
  crisis_id: any;
  duration_timer_began_at: any;
  ended_at: any;
  id: number;
  listener_mobile_phone: string;
  listener_role: ListenerRole;
  listener_role_id: number;
  listener_state_id: number;
  logged_duration: number;
  package_id: number;
  partner_code: PartnerCode;
  pending_announcement: string;
  pending_announcement_all: any;
  phone_number: string;
  rating_caller_of_listener: any;
  recording_path: any;
  request: Request;
  state: State;
  state_id: number;
  stripe_payment_intent_id: any;
  termination_disposition: any;
  tier_id: number;
}

export interface CallerRole {
  id: number;
  user: User;
}

export interface PartnerCode {
  default_promo_audience_type: string;
  incentives_reward_image_url: any;
  incentives_reward_pto_image_url: any;
  is_default_promo: boolean;
  logo_file_path: string;
  logo_file_url: string;
  partner: Partner;
  promo_button_label: string;
  promo_image_file_path: string;
  promo_image_url: string;
  promo_placement: string[];
  promo_type: string;
  promo_url: string;
  promo_video_file_path: any;
  promo_video_thumbnail_file_path: string;
  promo_video_thumbnail_url: string;
  promo_video_url: any;
  url: string;
}

export interface Partner {
  comms_logo_file_url: any;
  logo_background_file_url: any;
  logo_file_path: string;
  logo_file_url: string;
  logo_overlay_file_url: any;
  name: string;
}

export interface Request {
  audio_file_url: any;
  call_id: number;
  caller_role_id: number;
  connections: Connection[];
  connections_processed_at: any;
  created_at: string;
  id: number;
  is_backup_request: boolean;
  original_backup_request_id: number;
  original_request_id: any;
  package_id: number;
  pending_morphii_data?: unknown;
  perform_backup_requests: boolean;
  status: string;
  tag_group_ids: any[];
  text: any;
  topic: any;
  topic_id: any;
  updated_at: any;
}

export interface State {
  id: number;
  sub_state_id: number;
  type: string;
}

export const useCall = (
  callId: number,
  options?: Omit<UseQueryOptions<Call, unknown, Call, QueryKey>, 'initialData' | 'queryFn' | 'queryKey'> & {
    initialData?: (() => undefined) | undefined;
  },
) => useQuery<Call>(['call', callId], () => getCall(callId), { ...options });

export const useActiveCalls = (callerRoleId?: number, options?: { refetchInterval?: number }) => {
  const queryClient = useQueryClient();

  return useQuery<ActiveCall[]>(['activeCalls'], () => getActiveCalls(callerRoleId!), {
    enabled: !!callerRoleId,
    refetchInterval: options?.refetchInterval,
    onSuccess: (activeCalls) => {
      if (activeCalls.length > 0) {
        const activeConnectionIds = activeCalls.flatMap((call) =>
          call.request.connections.map((connection) => connection.id),
        );
        // Optimistically remove active call requests from cache
        queryClient.setQueryData<CallRequest[]>(callQueryKeys.callRequests, (oldData) => {
          return (
            oldData?.filter(
              (request) => !request.connections.some((connection) => activeConnectionIds.includes(connection.id)),
            ) || []
          );
        });
        // Also invalidate callRequests to ensure fresh data from server
        queryClient.invalidateQueries(callQueryKeys.callRequests);
      }
    },
  });
};

// returns true if listener and member both available; else false; undefined if requests in flight
export const useIsEverybodyAvailable = (isPeerAvailable: boolean) => {
  const { user, isLoading: userIsLoading } = useAuth();
  const { data: activeCalls, isLoading: isCallsLoading } = useActiveCalls(user?.caller_role_id);
  if (userIsLoading || isCallsLoading) {
    return undefined;
  }
  const isMemberAvailable = !activeCalls?.length;
  return isMemberAvailable && isPeerAvailable;
};

export const useAllCalls = (callerRoleId?: number) =>
  useQuery<ActiveCall[]>(['allCalls'], () => getAllCalls(callerRoleId!), {
    enabled: !!callerRoleId,
  });

export const useCallRequests = (callerRoleId?: number) =>
  useQuery<CallRequest[]>(callQueryKeys.callRequests, () => getCallRequests(callerRoleId!), {
    enabled: !!callerRoleId,
  });

// Hook to poll a single call request's status - useful for detecting passed calls
export const useCallRequestStatus = (requestId?: number, options?: { enabled?: boolean; refetchInterval?: number }) =>
  useQuery<CallRequest>(['callRequestStatus', requestId], () => getCallRequestById(requestId!), {
    enabled: !!requestId && options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    // Don't retry on 404 (request may have been deleted/cancelled)
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });

export interface CallRequestPayload {
  call_units_length: number | null;
  caller_role_id: number;
  text?: string;
  perform_backup_requests: boolean;
  original_request_id: number | null;
  scheduled_at?: string;
  tag_group_ids: number[];
  begin_call_mood_score?: number;
}

export const useCallRequestMutation = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const hubspotMutation = useHubspotEventMutation();
  return useMutation(
    ({
      listenerRoleId,
      payload,
      location,
      peerName,
      listenerImage,
    }: {
      listenerRoleId: number;
      payload: CallRequestPayload;
      location: string;
      peerName: string;
      listenerImage?: string;
    }) => {
      return axiosPost(`/requests/?listener_role_id=${listenerRoleId}`, payload).then((response) => ({
        response: response.data,
        metadata: { location, peerName, payload, listenerRoleId, listenerImage },
      }));
    },
    {
      onSuccess: ({ response, metadata: { location, peerName, payload, listenerRoleId, listenerImage } }) => {
        // Construct call request with connections for immediate cache update
        const now = new Date().toISOString();
        const isScheduledCall = !!payload.scheduled_at;

        // Build a synthetic connections array if the response doesn't have one
        const callRequestWithConnections: CallRequest = {
          ...response,
          connections:
            response.connections?.length > 0
              ? response.connections
              : [
                  {
                    id: 0, // Temporary ID until server returns real one
                    request_id: response.id,
                    listener_role_id: listenerRoleId,
                    listener_role: {
                      id: listenerRoleId,
                      user: {
                        id: 0,
                        display_name: peerName,
                        first_name: peerName.split(' ')[0] || peerName,
                      },
                      profile_photo_file_url: listenerImage || '',
                      profile_photo_square_file_url: listenerImage || '',
                      about_me: '',
                      availabilities: [],
                      available: false,
                      available_now: false,
                      background_traits: [],
                      can_take_calls: false,
                      connection_blocked: false,
                      favorite_id: 0,
                      is_available: false,
                      is_active: false,
                      is_peer_active: false,
                      listener_audio: [],
                      military_tag: '',
                      next_available_text: '',
                      peer_status: '',
                      primary_language_tag: '',
                      profile_traits: [],
                      secondary_language_tag: '',
                      share_link_url: '',
                      spirituality_tag: '',
                      state: '',
                      status: '',
                      tier_id: 0,
                      type: '',
                    } as any,
                    status: isScheduledCall ? 'offered' : 'matched',
                    scheduled_at: payload.scheduled_at || null,
                    created_at: now,
                    updated_at: now,
                    offered_at: now,
                    evaluation_ends_at: now,
                    expired: false,
                    listener_acknowledged: false,
                    package_id: payload.call_units_length ? response.package_id : null,
                    tier_id: 0,
                  },
                ],
        };

        // Immediately inject the new call request into the cache for instant UI update
        queryClient.setQueryData<CallRequest[]>(callQueryKeys.callRequests, (oldData) => {
          if (!oldData) return [callRequestWithConnections];
          // Add the new call request to the beginning of the list
          return [callRequestWithConnections, ...oldData];
        });

        // Also invalidate to ensure fresh data from server (background refetch)
        queryClient.invalidateQueries(callQueryKeys.callRequests);
        // Also invalidate active calls in case the request immediately becomes active
        queryClient.invalidateQueries(['activeCalls']);

        hubspotMutation.mutate({
          event: 'schedule_call',
          properties: {
            call_type: payload.scheduled_at ? 'scheduled' : 'now',
            client: user!.client_name,
            email_address: user!.email_address,
            location: location,
            peer_name: peerName,
            scheduled_at: payload.scheduled_at || 'now',
          },
        });
        analytics.trackEvent(
          EventTypes.SCHEDULE_CALL,
          payload.scheduled_at ? 'scheduled' : 'now',
          payload.tag_group_ids,
        );
        return response;
      },
    },
  );
};

export interface CreateCallRequestResponse {
  audio_duration?: number;
  audio_file_url: string;
  call_id: number;
  caller_role_id: number;
  connections_processed_at: number;
  created_at: string;
  id: number;
  is_backup_request: boolean;
  original_backup_request_id: number;
  original_request_id: number;
  package_id: number;
  partner_code: string;
  pending_morphii_data?: unknown;
  perform_backup_requests: boolean;
  status: string;
  text: string;
  topic: CreateCallRequestTopic;
  topic_id: number;
  updated_at: string;
}

export interface CreateCallRequestTopic {
  audio_file_url?: string;
  id: number;
  is_shadow_topic: boolean;
  morphii?: string[];
  morphii_metadata?: unknown[];
  name: string;
  theme: string;
}

export const useRequestCancelMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (requestId: number) => {
      return axiosPost(`/requests/${requestId}/cancel`, {});
    },
    {
      onMutate: async (requestId: number) => {
        // Cancel any outgoing refetches to prevent overwriting optimistic update
        await queryClient.cancelQueries(callQueryKeys.callRequests);

        // Snapshot the previous value
        const previousCallRequests = queryClient.getQueryData<CallRequest[]>(callQueryKeys.callRequests);

        // Optimistically remove the canceled call from the cache
        if (previousCallRequests) {
          queryClient.setQueryData<CallRequest[]>(
            callQueryKeys.callRequests,
            previousCallRequests.filter((request) => request.id !== requestId),
          );
        }

        // Return context with previous value for rollback
        return { previousCallRequests };
      },
      onError: (_error, _requestId, context) => {
        // Rollback to previous value on error
        if (context?.previousCallRequests) {
          queryClient.setQueryData(callQueryKeys.callRequests, context.previousCallRequests);
        }
      },
      onSettled: () => {
        // Always invalidate to ensure fresh data from server
        queryClient.invalidateQueries(callQueryKeys.callRequests);
      },
    },
  );
};

export const downloadCallCalendar = async (requestId: number): Promise<void> => {
  const domain = process.env.REACT_APP_DOMAIN_NAME;
  const apiVersion = process.env.REACT_APP_API_VERSION2;
  const domainUrl = `${domain}${apiVersion}`;

  const authorization_token = localStorage.getItem(StickyStateKeys.AuthorizationToken);
  const authorization_guest_token = localStorage.getItem('authorization_guest_token');

  const response = await fetch(`${domainUrl}/requests/${requestId}/calendar`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${authorization_token || authorization_guest_token}`,
      Accept: 'text/calendar, application/json, */*',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to download calendar: ${response.statusText}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'call-invite.ics';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};
